import { DonutChart, type DonutSlice } from "@/company/components/DonutChart";
import { CompanyMonthChart } from "@/company/components/CompanyMonthChart";
import { PaceGauge } from "@/company/components/PaceGauge";
import { TeamPaceBars } from "@/company/components/TeamPaceBars";
import { useCompanyData } from "@/company/hooks/useCompanyData";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageShell } from "@/components/layout/PageHeader";
import { Card, CardBody, CardHeader, EmptyState, InlineError, Spinner } from "@/components/ui";
import { getDepartment, isDepartmentId } from "@/grid/config/registry";
import { salesDepartment } from "@/grid/config/departments/sales";
import { useMemo, type ReactNode } from "react";
import { Navigate, useParams } from "react-router-dom";

function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 className="text-sm font-semibold text-[var(--color-text)]">{children}</h2>;
}

const cardHeaderClass = "border-b-0 px-6 pb-0 pt-5";

export function CompanyPage() {
  const { departmentId = "sales" } = useParams<{ departmentId: string }>();
  const department =
    isDepartmentId(departmentId) && departmentId === "sales"
      ? (getDepartment(departmentId) ?? salesDepartment)
      : salesDepartment;

  const { data, isLoading, isError, error } = useCompanyData(department);

  const donutSlices: DonutSlice[] = useMemo(
    () =>
      (data?.teams ?? []).map((team) => ({
        key: team.colorKey,
        label: team.teamName,
        value: team.qtdActual,
        color: team.color,
        sharePct: team.qtdSharePct,
      })),
    [data?.teams],
  );

  if (!isDepartmentId(departmentId) || departmentId !== "sales") {
    return <Navigate to="/company/sales" replace />;
  }

  return (
    <AppLayout>
      <PageShell>
        {isLoading ? (
          <div className="flex justify-center py-24">
            <Spinner />
          </div>
        ) : null}

        {isError ? (
          <InlineError message={error?.message ?? "Unable to load company data."} />
        ) : null}

        {!isLoading && !isError && data ? (
          <div className="space-y-6">
            <div className="grid gap-6 xl:grid-cols-2">
              <Card>
                <CardHeader className={cardHeaderClass}>
                  <SectionTitle>QTD Sales</SectionTitle>
                </CardHeader>
                <CardBody className="p-6 pt-4 sm:p-8 sm:pt-5">
                  <PaceGauge summary={data.qtd} showTitle={false} />
                </CardBody>
              </Card>
              <Card>
                <CardHeader className={cardHeaderClass}>
                  <SectionTitle>YTD Sales</SectionTitle>
                </CardHeader>
                <CardBody className="p-6 pt-4 sm:p-8 sm:pt-5">
                  <PaceGauge summary={data.ytd} showTitle={false} />
                </CardBody>
              </Card>
            </div>

            <div className="grid gap-6 xl:grid-cols-2">
              <Card>
                <CardHeader className={cardHeaderClass}>
                  <SectionTitle>QTD contribution</SectionTitle>
                </CardHeader>
                <CardBody className="p-6 pt-4">
                  <DonutChart slices={donutSlices} />
                </CardBody>
              </Card>

              <Card className="flex flex-col">
                <CardHeader className={cardHeaderClass}>
                  <SectionTitle>Team QTD pace</SectionTitle>
                </CardHeader>
                <CardBody className="flex flex-1 p-6 pt-4">
                  {data.teams.length > 0 ? (
                    <TeamPaceBars teams={data.teams} className="flex-1" />
                  ) : (
                    <EmptyState title="No teams" description="No active sales teams found." />
                  )}
                </CardBody>
              </Card>
            </div>

            <div className="grid gap-6 xl:grid-cols-2">
              <Card>
                <CardHeader className={cardHeaderClass}>
                  <SectionTitle>This quarter by month</SectionTitle>
                </CardHeader>
                <CardBody className="p-6 pt-4">
                  <CompanyMonthChart snapshots={data.quarterSnapshots} />
                </CardBody>
              </Card>

              <Card>
                <CardHeader className={cardHeaderClass}>
                  <SectionTitle>This year by month</SectionTitle>
                </CardHeader>
                <CardBody className="p-6 pt-4">
                  <CompanyMonthChart snapshots={data.monthlySnapshots} />
                </CardBody>
              </Card>
            </div>
          </div>
        ) : null}
      </PageShell>
    </AppLayout>
  );
}
